/**
 * Integrytas Group — shared translation-row presentation (§17.B14).
 *
 * The DATA half of the Translations related-children group: filtering, the locale
 * flag, and the language name resolved into the reader's own language. Both
 * presentation layers build their group from this, so a code's translations read
 * identically on desktop and on mobile.
 *
 * It lives in `src/lib/` — not beside either panel — because §16 lets the mobile
 * app share the data/logic layer while keeping its own presentation. Importing a
 * desktop component module from `src/mobile/` would breach that; importing a pure
 * function with no JSX and no platform types does not.
 */
import { localeFlag, localeName } from '../i18n/locale_display';

/** The shape every `<entity>_tl` row shares. Pages may carry extra columns. */
export interface TranslationLike {
  locale_code: string;
  display_text: string;
}

/** Platform-neutral: each layer maps `hover` onto its own affordance. */
export interface TranslationItem {
  id: string;
  label: string;
  /** "British English — EN_GB". A tooltip on desktop; a visible line on touch. */
  hover: string;
  /** Locale flag emoji. */
  icon: string;
}

/**
 * @param tl     the row's `_tl` records (safe to pass undefined/null)
 * @param locale the ACTIVE CSM locale, so each language is named in the reader's
 *               own language (Intl.DisplayNames)
 */
export function translationItems(
  tl: TranslationLike[] | null | undefined,
  locale: string,
): TranslationItem[] {
  return (tl ?? [])
    // A `_tl` row with no text is a gap, not a translation — rendering it as a
    // blank line would read as "translated into blank".
    .filter(x => x?.locale_code && x.display_text?.trim())
    .map(x => ({
      id: x.locale_code,
      label: x.display_text,
      hover: `${localeName(x.locale_code, locale)} — ${x.locale_code}`,
      icon: localeFlag(x.locale_code),
    }))
    // Stable order by locale code. Without this the list follows whatever order the
    // gateway returned, so the same code can list its locales differently on two
    // screens — or on two visits — which reads as arbitrary.
    .sort((a, b) => a.id.localeCompare(b.id));
}
